import { Breadcrumb, Button, Label, TextInput, Card } from "flowbite-react";
import { useEffect, useRef, useState, type FC } from "react";
import { HiHome, HiChevronDown, HiUsers, HiSearch, HiFilter, HiX } from "react-icons/hi";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import { AddContactModal, AllContactsTable, Pagination } from "../components/contactsList";
import { useLocation, useNavigate } from "react-router";
import { useAppContext } from "../context/AppContext";
import Select from "react-select";
import { User } from "../services/user";
import { companyService } from "../services/main";

const ContactsPage: FC = function () {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{ value: any; label: any }[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedNewsletter, setSelectedNewsletter] = useState<string>("");
  const location = useLocation();
  const stateData = location.state;
  const { contacts, setContacts, companies, setCompanies } = useAppContext();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [filters, setFilters] = useState({
    companyId: "",
    status: "",
    hasNewsletter: "",
    accountManagerId: "",
  }) as any;
  const dropdownRef = useRef(null) as any;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userService = new User("/users");
        const response = await userService.getAllUsers();
        setUsers(response.data.users || response.data || []);
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!companies || companies.length === 0) {
        try {
          const response = await companyService.listCompanies();
          setCompanies(response.data.companies || []);
        } catch (error) {
          console.error("Failed to fetch companies", error);
        }
      }
    };
    fetchCompanies();
  }, []);

  const toggleFilterDropdown = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsFilterOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredContacts = contacts.filter((contact: any) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (contact.firstName?.toLowerCase().includes(searchLower)) ||
        (contact.lastName?.toLowerCase().includes(searchLower)) ||
        (contact.email?.toLowerCase().includes(searchLower)) ||
        (contact.title?.toLowerCase().includes(searchLower)) ||
        (contact.company?.name?.toLowerCase().includes(searchLower)) ||
        (contact.phoneNumbers?.some((p: any) => p.number?.toLowerCase().includes(searchLower)));
      if (!matchesSearch) return false;
    }

    if (selectedUsers.length > 0) {
      const matchesUser = selectedUsers.some(u => contact.accountManagerId === u.value);
      if (!matchesUser) return false;
    }

    if (selectedCompany) {
      const companyId = contact.company?.id || contact.companyId;
      if (companyId !== Number(selectedCompany)) return false;
    }

    if (selectedStatus) {
      if (contact.status !== selectedStatus) return false;
    }

    if (selectedNewsletter !== "") {
      const hasNewsletter = contact.hasNewsletter === true;
      if (selectedNewsletter === "yes" && !hasNewsletter) return false;
      if (selectedNewsletter === "no" && hasNewsletter) return false;
    }

    return true;
  });

  const totalItems = filteredContacts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedUsers, selectedCompany, selectedStatus, selectedNewsletter]);

  return (
    <>
      <NavbarSidebarLayout>
        <div className="px-4 pt-6 pb-8">
          {/* Header Section */}
          <div className="mb-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item onClick={() => navigate("/")}>
                <div className="flex items-center gap-x-3 cursor-pointer">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Contacts</Breadcrumb.Item>
            </Breadcrumb>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <HiUsers className="text-blue-600 dark:text-blue-400" />
                  All Contacts
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Showing {filteredContacts.length} of {contacts.length} contacts
                  {searchTerm && ` matching "${searchTerm}"`}
                </p>
              </div>
            </div>
          </div>

          {/* Search and Actions */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="w-full sm:w-auto">
              <Label htmlFor="users-search" className="sr-only">
                Search
              </Label>
              <div className="relative lg:w-64 xl:w-96">
                <TextInput
                  id="users-search"
                  name="users-search"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <AddContactModal />
              <div className="relative">
                <Button 
                  color="gray" 
                  type="button" 
                  onClick={toggleFilterDropdown}
                  className="hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    className="h-4 w-4 mr-2 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Filter
                  {(selectedUsers.length > 0 || selectedCompany || selectedStatus || selectedNewsletter) && (
                    <span className="ml-2 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                      {[
                        selectedUsers.length > 0 ? selectedUsers.length : 0,
                        selectedCompany ? 1 : 0,
                        selectedStatus ? 1 : 0,
                        selectedNewsletter ? 1 : 0
                      ].reduce((a, b) => a + b, 0)}
                    </span>
                  )}
                  <HiChevronDown className="ml-1.5 h-5 w-5" />
                </Button>
                {(selectedUsers.length > 0 || selectedCompany || selectedStatus || selectedNewsletter) && (
                  <button
                    className="absolute -top-1 -right-1 text-gray-500 hover:text-red-600 bg-white dark:bg-gray-800 rounded-full w-5 h-5 flex items-center justify-center text-xs border border-gray-200 dark:border-gray-700"
                    onClick={() => {
                      setSelectedUsers([]);
                      setSelectedCompany("");
                      setSelectedStatus("");
                      setSelectedNewsletter("");
                    }}
                    title="Clear all filters"
                  >
                    &#10005;
                  </button>
                )}
                {isFilterOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-20 mt-2 right-0 bg-white dark:bg-gray-800 border-0 rounded-xl shadow-xl p-6 min-w-[300px] max-w-[400px]"
                  >
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block text-sm font-medium">Account Manager</Label>
                        <Select
                          options={users.map((u: any) => ({
                            value: u.id,
                            label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || `User #${u.id}`
                          }))}
                          value={selectedUsers}
                          onChange={(vals) => setSelectedUsers(vals as any[])}
                          isMulti
                          isClearable={true}
                          placeholder="Select account managers..."
                          className="w-full text-sm"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </div>

                      <div>
                        <Label className="mb-2 block text-sm font-medium">Company</Label>
                        <select
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={selectedCompany}
                          onChange={(e) => setSelectedCompany(e.target.value)}
                        >
                          <option value="">All Companies</option>
                          {companies.map((company: any) => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label className="mb-2 block text-sm font-medium">Status</Label>
                        <select
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="NEW">New</option>
                          <option value="CONTACTED">Contacted</option>
                          <option value="QUALIFIED">Qualified</option>
                          <option value="LOST">Lost</option>
                          <option value="WON">Won</option>
                        </select>
                      </div>

                      <div>
                        <Label className="mb-2 block text-sm font-medium">Newsletter Subscription</Label>
                        <select
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={selectedNewsletter}
                          onChange={(e) => setSelectedNewsletter(e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="yes">Subscribed</option>
                          <option value="no">Not Subscribed</option>
                        </select>
                      </div>

                      <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          color="gray"
                          size="xs"
                          onClick={() => {
                            setSelectedUsers([]);
                            setSelectedCompany("");
                            setSelectedStatus("");
                            setSelectedNewsletter("");
                          }}
                          className="flex-1"
                        >
                          Clear All
                        </Button>
                        <Button
                          color="primary"
                          size="xs"
                          onClick={() => setIsFilterOpen(false)}
                          className="flex-1"
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <AllContactsTable paginatedContacts={paginatedContacts} searchTerm={searchTerm} />
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </NavbarSidebarLayout>
    </>
  );
};

export default ContactsPage;
